import { useState, useCallback, useRef, type DragEvent, type ChangeEvent } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
} from '@mui/material';
import {
  CloudUpload,
  InsertDriveFile,
  Delete,
  CheckCircle,
  ErrorOutlined,
} from '@mui/icons-material';
import { detectFormat } from '../../services/formatDetection';
import type { GISFormat, FormatDetectionResult } from '../../types/gisFormat';

/**
 * Supported file extensions for display to the user.
 */
const SUPPORTED_FORMATS: { format: GISFormat; label: string; extensions: string }[] = [
  { format: 'kml', label: 'KML', extensions: '.kml, .kmz' },
  { format: 'shapefile', label: 'Shapefile', extensions: '.shp, .zip' },
  { format: 'geopackage', label: 'GeoPackage', extensions: '.gpkg' },
  { format: 'geojson', label: 'GeoJSON', extensions: '.geojson, .json' },
  { format: 'gpx', label: 'GPX', extensions: '.gpx' },
];

/**
 * Accepted file extensions for the file input.
 */
const ACCEPTED_EXTENSIONS = '.kml,.kmz,.shp,.shx,.dbf,.prj,.cpg,.zip,.gpkg,.geojson,.json,.gpx';

/**
 * Represents a selected file with its detection result.
 */
export interface SelectedFile {
  file: File;
  detectionResult: FormatDetectionResult | null;
  error: string | null;
  detecting: boolean;
}

export interface FileUploadProps {
  /** Callback when the user clicks Import with valid files */
  onImport: (files: SelectedFile[]) => void;
  /** Whether the import is currently in progress */
  importing?: boolean;
  /** Whether to allow multiple file selection */
  multiple?: boolean;
}

/**
 * Formats a file size in bytes to a human-readable string.
 */
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = bytes / Math.pow(1024, i);
  return `${size.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

/**
 * Returns a display label for a detected GIS format.
 */
function getFormatLabel(format: GISFormat): string {
  const entry = SUPPORTED_FORMATS.find((f) => f.format === format);
  return entry?.label ?? format.toUpperCase();
}

/**
 * Returns a color for the confidence chip.
 */
function getConfidenceColor(confidence: string): 'success' | 'warning' | 'default' {
  switch (confidence) {
    case 'high':
      return 'success';
    case 'medium':
      return 'warning';
    default:
      return 'default';
  }
}

/**
 * FileUpload Component
 *
 * Provides a drag-and-drop file upload area with click-to-browse support.
 * Detects the GIS format of uploaded files and displays file information
 * including name, size, and detected format with confidence indicator.
 *
 * Features:
 * - Drag-and-drop file upload area
 * - Click-to-browse file selection
 * - Automatic format detection using formatDetection service
 * - File name, size, and format display
 * - Supported formats list
 * - Import button to trigger import process
 * - Loading state during format detection
 * - Multiple file selection support (optional)
 */
const FileUpload: React.FC<FileUploadProps> = ({
  onImport,
  importing = false,
  multiple = true,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * Processes files by running format detection on each.
   */
  const processFiles = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    if (fileArray.length === 0) return;

    // Create initial entries with detecting state
    const initialEntries: SelectedFile[] = fileArray.map((file) => ({
      file,
      detectionResult: null,
      error: null,
      detecting: true,
    }));

    setSelectedFiles((prev) => [...prev, ...initialEntries]);

    // Run format detection for each file
    for (let i = 0; i < fileArray.length; i++) {
      const file = fileArray[i];
      try {
        const result = await detectFormat(file);
        setSelectedFiles((prev) =>
          prev.map((entry) =>
            entry.file === file ? { ...entry, detectionResult: result, detecting: false } : entry
          )
        );
      } catch (err: unknown) {
        const errorMessage =
          err instanceof Error ? err.message : `Failed to detect format for "${file.name}"`;
        setSelectedFiles((prev) =>
          prev.map((entry) =>
            entry.file === file ? { ...entry, error: errorMessage, detecting: false } : entry
          )
        );
      }
    }
  }, []);

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      const { files } = e.dataTransfer;
      if (files && files.length > 0) {
        if (multiple) {
          processFiles(files);
        } else {
          processFiles([files[0]]);
        }
      }
    },
    [multiple, processFiles]
  );

  const handleFileInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { files } = e.target;
      if (files && files.length > 0) {
        processFiles(files);
      }
      // Reset input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [processFiles]
  );

  const handleBrowseClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleRemoveFile = useCallback((fileToRemove: File) => {
    setSelectedFiles((prev) => prev.filter((entry) => entry.file !== fileToRemove));
  }, []);

  const handleImport = useCallback(() => {
    const validFiles = selectedFiles.filter(
      (entry) => entry.detectionResult !== null && !entry.detecting
    );
    if (validFiles.length > 0) {
      onImport(validFiles);
    }
  }, [selectedFiles, onImport]);

  const hasValidFiles = selectedFiles.some(
    (entry) => entry.detectionResult !== null && !entry.detecting
  );
  const isDetecting = selectedFiles.some((entry) => entry.detecting);

  return (
    <Box sx={{ width: '100%', maxWidth: 600 }}>
      {/* Drop Zone */}
      <Paper
        data-testid="drop-zone"
        elevation={isDragOver ? 4 : 1}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleBrowseClick}
        sx={{
          p: 4,
          textAlign: 'center',
          cursor: 'pointer',
          border: '2px dashed',
          borderColor: isDragOver ? 'primary.main' : 'divider',
          backgroundColor: isDragOver ? 'action.hover' : 'background.paper',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            borderColor: 'primary.light',
            backgroundColor: 'action.hover',
          },
        }}
      >
        <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
        <Typography variant="h6" gutterBottom>
          Drag & drop GIS files here
        </Typography>
        <Typography variant="body2" color="text.secondary">
          or click to browse files
        </Typography>
      </Paper>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_EXTENSIONS}
        multiple={multiple}
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
        data-testid="file-input"
      />

      {/* Supported Formats */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
          Supported formats:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {SUPPORTED_FORMATS.map(({ label, extensions }) => (
            <Chip key={label} label={`${label} (${extensions})`} size="small" variant="outlined" />
          ))}
        </Box>
      </Box>

      {/* Selected Files List */}
      {selectedFiles.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Selected Files
          </Typography>
          <List dense>
            {selectedFiles.map((entry, index) => (
              <ListItem
                key={`${entry.file.name}-${index}`}
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label={`Remove ${entry.file.name}`}
                    onClick={() => handleRemoveFile(entry.file)}
                    size="small"
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                }
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {entry.detecting ? (
                    <CircularProgress size={20} data-testid="detecting-spinner" />
                  ) : entry.error ? (
                    <ErrorOutlined color="error" fontSize="small" />
                  ) : (
                    <CheckCircle color="success" fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InsertDriveFile fontSize="small" color="action" />
                      <Typography variant="body2" noWrap>
                        {entry.file.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ({formatFileSize(entry.file.size)})
                      </Typography>
                    </Box>
                  }
                  secondary={
                    entry.detecting ? (
                      <Typography variant="caption" color="text.secondary">
                        Detecting format...
                      </Typography>
                    ) : entry.error ? (
                      <Typography variant="caption" color="error">
                        {entry.error}
                      </Typography>
                    ) : entry.detectionResult ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <Chip
                          label={getFormatLabel(entry.detectionResult.format)}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                        <Chip
                          label={`${entry.detectionResult.confidence} confidence`}
                          size="small"
                          color={getConfidenceColor(entry.detectionResult.confidence)}
                          variant="outlined"
                        />
                      </Box>
                    ) : null
                  }
                />
              </ListItem>
            ))}
          </List>

          {/* Error alert for files with detection errors */}
          {selectedFiles.some((entry) => entry.error) && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Some files could not be recognized. Only files with detected formats will be imported.
            </Alert>
          )}
        </Box>
      )}

      {/* Import Button */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleImport}
          disabled={!hasValidFiles || isDetecting || importing}
          startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <CloudUpload />}
        >
          {importing ? 'Importing...' : 'Import'}
        </Button>
      </Box>
    </Box>
  );
};

export default FileUpload;
